import { useState, useCallback, useEffect, useRef, Component, type ErrorInfo, type ReactNode } from 'react'
import { BookOpen, BookText, X, AlertTriangle, Search, Star, Command, BookMarked, PanelRightOpen, Menu, Newspaper } from 'lucide-react'
import { Sidebar } from './components/Sidebar'
import { Chat } from './components/Chat'
import { Landing } from './components/Landing'
import { Login } from './components/Login'
import { Settings } from './components/Settings'
import { KnowledgeBase } from './components/KnowledgeBase'
import { ScriptureSearch } from './components/ScriptureSearch'
import { CommandPalette } from './components/CommandPalette'
import { CrossReference } from './components/CrossReference'
import { BlogList } from './components/BlogList'
import { BlogPost as BlogPostComponent } from './components/BlogPost'
import { useAuth } from './lib/auth-context'
import type { Conversation, BlogPost } from './lib/types'
import { generateId } from './lib/utils'
import { supabase } from './lib/supabase'
import { configureVectorDb } from './lib/vector-db'
import { logger, setLoggerUserId } from './lib/logger'

type Page = 'landing' | 'login' | 'chat'

const GUEST_CONVERSATIONS_KEY = 'al-munazir-guest-conversations'

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.captureException(error, {
      componentStack: errorInfo.componentStack,
      source: 'ErrorBoundary',
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center bg-[hsl(var(--bg))] px-4">
          <div className="card max-w-md p-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--error-bg))]">
              <AlertTriangle className="h-6 w-6 text-[hsl(var(--error))]" />
            </div>
            <h2 className="text-h4 mb-2 text-[hsl(var(--fg))]">Something went wrong</h2>
            <p className="text-body text-[hsl(var(--fg-muted))] mb-6">
              An unexpected error occurred. Please refresh the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="btn-cta"
            >
              Refresh Page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

type Tab = 'chat' | 'quran' | 'knowledge' | 'cross-ref' | 'blog'

function App() {
  const { user, profile, settings: userSettings, loading } = useAuth()
  const [page, setPage] = useState<Page>('landing')

  useEffect(() => {
    configureVectorDb('https://riddwam-al-munazir-ai.hf.space/search')
    logger.info('App mounted', {
      page: 'landing',
      hasUser: !!user,
      hasProfile: !!profile,
    })
  }, [])

  useEffect(() => {
    setLoggerUserId(user?.id || null)
  }, [user])

  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentId, setCurrentId] = useState<string | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showRules, setShowRules] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showCommandPalette, setShowCommandPalette] = useState(false)
  const [showCrossReference, setShowCrossReference] = useState(false)
  const [selectedModel, setSelectedModel] = useState(() => localStorage.getItem('islam-preacher-model') || 'qwen/qwen3.5-397b-a17b')
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('islam-preacher-api-key') || '')
  const [chatMode, setChatMode] = useState<'learning' | 'debating'>('debating')
  const [creatingConversation, setCreatingConversation] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>('chat')
  const [selectedBlogPost, setSelectedBlogPost] = useState<BlogPost | null>(null)
  const [isGuest, setIsGuest] = useState(() => localStorage.getItem('al-munazir-guest') === 'true')

  useEffect(() => {
    if (userSettings?.openrouter_key) {
      setApiKey(userSettings.openrouter_key)
      localStorage.setItem('islam-preacher-api-key', userSettings.openrouter_key)
    }
  }, [userSettings])

  useEffect(() => {
    if (!loading) {
      if (user && profile?.religion) setPage('chat')
      else if (user) setPage('login')
      else if (isGuest) setPage('chat')
      else setPage('landing')
    }
  }, [user, profile, loading, isGuest])

  useEffect(() => {
    if (!user) return
    supabase.from('chat_sessions').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).then(({ data }) => {
      if (data) {
        setConversations(data.map(s => ({
          id: s.id,
          title: s.title,
          messages: [],
          createdAt: new Date(s.created_at).getTime(),
          updatedAt: new Date(s.updated_at).getTime(),
          mode: s.mode || 'debating',
        })))
      }
    })
  }, [user])

  // Persist guest conversations to localStorage
  useEffect(() => {
    if (!isGuest || conversations.length === 0) return
    const timer = setTimeout(() => {
      const toSave = conversations.map(c => ({ id: c.id, title: c.title, createdAt: c.createdAt, updatedAt: c.updatedAt, mode: c.mode }))
      localStorage.setItem(GUEST_CONVERSATIONS_KEY, JSON.stringify(toSave))
    }, 500)
    return () => clearTimeout(timer)
  }, [conversations, isGuest])

  const currentConversation = conversations.find(c => c.id === currentId)

  const handleNewConversation = useCallback(() => {
    if (!user && !isGuest) return
    const newConv: Conversation = {
      id: generateId(),
      title: 'New Conversation',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
      mode: chatMode,
    }
    setConversations(prev => [newConv, ...prev])
    setCurrentId(newConv.id)
    setSidebarOpen(false)
  }, [user, chatMode])

  useEffect(() => {
    if (page === 'chat' && user && !currentId) {
      handleNewConversation()
    }
  }, [page, user, currentId, handleNewConversation])

  const handleConversationCreated = useCallback(async (id: string) => {
    if (!user) return
    const conv = conversations.find(c => c.id === id)
    if (!conv) return
    await supabase.from('chat_sessions').upsert({
      id,
      user_id: user.id,
      mode: conv.mode || chatMode,
      title: 'New Conversation',
    }).then(({ error }) => {
      if (error) console.error('Failed to create conversation in DB', error)
    })
  }, [user, conversations, chatMode])

  const handleSelectConversation = useCallback(async (id: string) => {
    setCurrentId(id)
    setSidebarOpen(false)
  }, [])

  const handleDeleteConversation = useCallback(async (id: string) => {
    setConversations(prev => prev.filter(c => c.id !== id))
    if (currentId === id) setCurrentId(null)
    await supabase.from('chat_messages').delete().eq('session_id', id)
    await supabase.from('chat_sessions').delete().eq('id', id)
  }, [currentId])

  const handleModelChange = useCallback((model: string) => {
    setSelectedModel(model)
    localStorage.setItem('islam-preacher-model', model)
  }, [])

  const handleApiKeyChange = useCallback((key: string) => {
    setApiKey(key)
    localStorage.setItem('islam-preacher-api-key', key)
  }, [])

  const handleProfileComplete = useCallback(() => {
    setPage('chat')
  }, [])

  const handleGuestStart = useCallback(() => {
    localStorage.setItem('al-munazir-guest', 'true')
    setIsGuest(true)
    const saved = localStorage.getItem(GUEST_CONVERSATIONS_KEY)
    if (saved) {
      try {
        setConversations(JSON.parse(saved))
        setCurrentId(JSON.parse(saved)[0]?.id || null)
      } catch {}
    }
    setPage('chat')
  }, [])

  const handleSignOut = useCallback(() => {
    localStorage.removeItem('al-munazir-guest')
    setIsGuest(false)
    setConversations([])
    setCurrentId(null)
  }, [])

  const handleCommandAction = useCallback((actionId: string) => {
    switch (actionId) {
      case 'new-chat': handleNewConversation(); break
      case 'switch-learn': setChatMode('learning'); setActiveTab('chat'); break
      case 'switch-debate': setChatMode('debating'); setActiveTab('chat'); break
      case 'go-home': setPage('landing'); break
      case 'go-scriptures': setActiveTab('quran'); break
      case 'go-knowledge': setActiveTab('knowledge'); break
      case 'go-blog': setActiveTab('blog'); break
      case 'compare': setShowCrossReference(true); break
    }
  }, [handleNewConversation])

  const handleSelectSurah = useCallback((number: number) => {
    setActiveTab('quran')
  }, [])

  const handleNavigate = useCallback((target: string) => {
    if (target === 'scholar') {
      setPage('chat')
      setActiveTab('chat')
    } else if (target === 'blog') {
      setPage('chat')
      setActiveTab('blog')
      setSelectedBlogPost(null)
    } else if (target === 'compare') {
      setPage('chat')
      setShowCrossReference(true)
    } else if (target === 'knowledge') {
      setPage('chat')
      setActiveTab('knowledge')
    }
  }, [])

  // Global keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (page === 'chat') setShowCommandPalette(p => !p)
      }
      if (e.key === 'Escape') {
        setShowCommandPalette(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [page])

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="stack items-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--accent-subtle))]">
            <BookOpen className="h-5 w-5 text-[hsl(var(--accent))]" />
          </div>
          <div className="h-1 w-32 overflow-hidden rounded-full bg-[hsl(var(--border))]">
            <div className="h-full w-1/2 rounded-full bg-[hsl(var(--accent))] animate-[loading_1s_ease-in-out_infinite]" />
          </div>
        </div>
      </div>
    )
  }

  if (page === 'landing') return <Landing onGetStarted={() => setPage('login')} onGuestStart={handleGuestStart} onNavigate={handleNavigate} />
  if (page === 'login') return <Login onComplete={handleProfileComplete} onGuestStart={handleGuestStart} />

  const tabs: { id: Tab; label: string; icon: typeof BookOpen }[] = [
    { id: 'chat', label: 'Scholar', icon: BookOpen },
    { id: 'quran', label: 'Quran', icon: BookText },
    { id: 'knowledge', label: 'Texts', icon: BookMarked },
    { id: 'blog', label: 'Blog', icon: Newspaper },
    { id: 'cross-ref', label: 'Compare', icon: Star },
  ]

  return (
    <ErrorBoundary>
    <div className="flex h-screen relative">
      <div className="app-geo-overlay" />
      <button
        onClick={() => setSidebarOpen(true)}
        className="btn-icon fixed left-3 top-3 z-30 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] lg:hidden"
        aria-label="Open sidebar"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 4.5h12M3 9h12M3 13.5h12" />
        </svg>
      </button>

      <Sidebar
        conversations={conversations}
        currentConversationId={currentId || undefined}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onDeleteConversation={handleDeleteConversation}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        isLoading={creatingConversation}
      />

      <main className="flex flex-1 flex-col min-w-0">
        <header className="topnav">
          <div className="topnav-inner px-4 lg:px-6 gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="btn-icon shrink-0 hidden lg:flex rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:bg-[hsl(var(--elevated))]"
                aria-label="Toggle sidebar"
              >
                <PanelRightOpen className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--accent-subtle))]">
                  <BookOpen className="h-4 w-4 text-[hsl(var(--accent))]" />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-small font-semibold text-[hsl(var(--fg))]">Al-Munazir AI</h1>
                  <p className="text-caption text-[hsl(var(--fg-muted))]">
                    {chatMode === 'learning' ? 'Learn Mode' : 'Debate Mode'}
                    {profile?.name ? ` · ${profile.name}` : ''}
                    {isGuest ? ' · Guest' : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="hidden md:flex items-center rounded-lg bg-[hsl(var(--elevated))] border border-[hsl(var(--border))] p-0.5">
              {tabs.map(({ id, label, icon: TabIcon }) => {
                const isSpecial = id === 'cross-ref'
                const isActive = isSpecial
                  ? false
                  : id === 'blog'
                    ? activeTab === 'blog' && !selectedBlogPost
                    : activeTab === id
                return (
                <button
                  key={id}
                  onClick={() => {
                    if (id === 'blog') setSelectedBlogPost(null)
                    setActiveTab(id)
                    if (id === 'cross-ref') setShowCrossReference(true)
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-caption font-medium rounded-md transition-all ${
                    isActive
                      ? 'bg-[hsl(var(--surface))] text-[hsl(var(--accent))] shadow-sm border border-[hsl(var(--border))]'
                      : 'text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))]'
                  }`}
                >
                  <TabIcon className="h-3.5 w-3.5" />
                  {label}
                </button>
                )
              })}
            </div>

            <div className="flex items-center gap-1.5 ml-auto shrink-0">
              {/* Search button */}
              <button
                onClick={() => setShowCommandPalette(true)}
                className="hidden sm:flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--elevated))]/50 px-3 py-1.5 text-caption text-[hsl(var(--fg-muted))] hover:border-[hsl(var(--accent)/0.3)] hover:text-[hsl(var(--fg))] transition-all"
              >
                <Search className="h-3.5 w-3.5" />
                <span className="hidden lg:inline">Search</span>
                <kbd className="ml-1 rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-1 py-0.5 text-[9px] font-mono">
                  {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}K
                </kbd>
              </button>
              <button
                onClick={() => setShowCommandPalette(true)}
                className="btn-ghost btn-icon sm:hidden"
                title="Search"
              >
                <Search className="h-4 w-4" />
              </button>

              <div className="flex rounded-md border border-[hsl(var(--border))] overflow-hidden">
                <button
                  onClick={() => { setChatMode('learning'); setActiveTab('chat') }}
                  className={`px-2.5 py-1.5 text-caption font-medium transition-all ${
                    chatMode === 'learning' && activeTab === 'chat' ? 'bg-[hsl(var(--accent))] text-white' : 'text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))]'
                  }`}
                >
                  Learn
                </button>
                <button
                  onClick={() => { setChatMode('debating'); setActiveTab('chat') }}
                  className={`px-2.5 py-1.5 text-caption font-medium transition-all ${
                    chatMode === 'debating' && activeTab === 'chat' ? 'bg-[hsl(var(--accent))] text-white' : 'text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))]'
                  }`}
                >
                  Debate
                </button>
              </div>

              <button
                onClick={() => setShowRules(true)}
                className="btn-ghost btn-icon"
                title="About"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="8" cy="8" r="6.5" />
                  <path d="M8 7.5v4M8 5v.5" strokeLinecap="round" />
                </svg>
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="btn-ghost btn-icon"
                title="Settings"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="8" cy="8" r="2" />
                  <path d="M8 1v2M8 13v2M1 8h2M13 8h2M2.93 2.93l1.41 1.41M11.66 11.66l1.41 1.41M2.93 13.07l1.41-1.41M11.66 4.34l1.41-1.41" />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile tab bar */}
          <div className="flex md:hidden border-t border-[hsl(var(--border))] px-2">
            {tabs.map(({ id, label, icon: TabIcon }) => {
              const isSpecial = id === 'cross-ref'
              const isActive = isSpecial
                ? false
                : id === 'blog'
                  ? activeTab === 'blog' && !selectedBlogPost
                  : activeTab === id
              return (
                <button
                  key={id}
                  onClick={() => {
                    if (id === 'blog') setSelectedBlogPost(null)
                    setActiveTab(id)
                    if (id === 'cross-ref') setShowCrossReference(true)
                  }}
                  className={`flex-1 flex flex-col items-center gap-0.5 py-2 text-caption font-medium transition-all ${
                    isActive
                      ? 'text-[hsl(var(--accent))] border-t-2 border-[hsl(var(--accent))]'
                      : 'text-[hsl(var(--fg-muted))] border-t-2 border-transparent'
                  }`}
                >
                  <TabIcon className="h-4 w-4" />
                  {label}
                </button>
              )
            })}
          </div>
        </header>

        <div className="relative flex-1 overflow-hidden">
          {activeTab === 'quran' ? (
            <ScriptureSearch />
          ) : activeTab === 'knowledge' ? (
            <KnowledgeBase />
          ) : activeTab === 'blog' ? (
            selectedBlogPost ? (
              <BlogPostComponent post={selectedBlogPost} onBack={() => setSelectedBlogPost(null)} />
            ) : (
              <BlogList onSelectPost={(post) => { setSelectedBlogPost(post); setActiveTab('blog') }} />
            )
          ) : (
            <Chat
              model={selectedModel}
              apiKey={apiKey}
              conversationId={currentId || undefined}
              mode={chatMode}
              conversations={conversations}
              userId={user?.id}
              isGuest={isGuest}
              onSignOut={handleSignOut}
              onConversationCreated={handleConversationCreated}
              onConversationUpdate={(messages) => {
                if (currentId) {
                  setConversations(prev =>
                    prev.map(c => c.id === currentId ? { ...c, messages, updatedAt: Date.now() } : c)
                  )
                }
              }}
            />
          )}
        </div>
      </main>

      {/* Global Command Palette */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onAction={handleCommandAction}
        onSelectSurah={handleSelectSurah}
        onInsertText={(text) => {
          const input = document.querySelector<HTMLTextAreaElement>('.chat-input-area')
          if (input) {
            input.value += text
            input.focus()
          }
        }}
      />

      {/* Cross-Reference Modal */}
      {showCrossReference && (
        <CrossReference
          onClose={() => setShowCrossReference(false)}
          onInsertText={(text) => {
            setActiveTab('chat')
          }}
        />
      )}

      {showRules && (
        <div className="modal-overlay" onClick={() => setShowRules(false)}>
          <div className="modal-panel modal-panel-md card p-0" onClick={e => e.stopPropagation()}>
            <div className="card-header sticky top-0">
              <h2 className="text-h4">About Al-Munazir AI</h2>
              <button onClick={() => setShowRules(false)} className="btn-ghost btn-icon" aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" />
                </svg>
              </button>
            </div>
            <div className="card-body prose prose-invert prose-sm max-w-none">
              <h3>Modes</h3>
              <p><strong>Learn Mode</strong> — Ask questions, explore Islamic theology, history, and scripture. The AI teaches with patience and depth.</p>
              <p><strong>Debate Mode</strong> — Challenge the AI's positions with rigorous, evidence-based argumentation: loaded question rejection, reciprocity, evidence-based arguments.</p>
              <h3>How Debate Mode Works</h3>
              <ul>
                <li><strong>No loaded questions:</strong> Refuses trap questions or false dilemmas.</li>
                <li><strong>Reciprocity:</strong> Expects you to answer his questions before he answers yours.</li>
                <li><strong>Definitional clarity:</strong> Will ask you to define terms.</li>
                <li><strong>Contradiction detection:</strong> Calls out inconsistencies.</li>
                <li><strong>Evidence-based:</strong> Cites Quran, Bible, history, logic.</li>
                <li><strong>Tawhid first:</strong> Core requirement is belief in One God.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <Settings
        open={showSettings}
        onClose={() => setShowSettings(false)}
        selectedModel={selectedModel}
        onModelChange={handleModelChange}
        apiKey={apiKey}
        onApiKeyChange={handleApiKeyChange}
      />
    </div>
    </ErrorBoundary>
  )
}

export default App
